import { useState, useEffect } from "react";
import {
  Card,
  Space,
  Button,
  Row,
  Col,
  Select,
  Input,
  Statistic,
  Tag,
} from "antd";
import {
  UnorderedListOutlined,
  PlusOutlined,
  SearchOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import FinanceService from "../../../dependencies/custom/financeService";
import { useFeeStructures } from "../../../hooks/finance/useFeeStructures";
import { useFeeTypes } from "../../../hooks/finance/useFeeTypes";
import FeeStructuresTable from "../../../components/finance/fees/FeeStructuresTable";
import FeeStructureModal from "../../../components/finance/fees/FeeStructureModal";
import FeeStructureDetailDrawer from "../../../components/finance/fees/FeeStructureDetailDrawer";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";
import { useNavigate } from "react-router-dom";
import useAcademicYears from "../../../hooks/common/useAcademicYears";

const { Option } = Select;

const FeeStructuresManagement = () => {
  const navigate = useNavigate();
  const {
    feeStructures,
    loading,
    fetchFeeStructures,
    deleteFeeStructure,
    activateFeeStructure,
    deactivateFeeStructure,
    fetchStudentsAssignedToFeeStructure,
  } = useFeeStructures();

  const { feeTypes, fetchFeeTypes } = useFeeTypes();
  const { academicYears, fetchAcademicYears } = useAcademicYears();

  const [modalVisible, setModalVisible] = useState(false);
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
  const [editingFeeStructure, setEditingFeeStructure] = useState(null);
  const [selectedFeeStructure, setSelectedFeeStructure] = useState(null);
  const [classes, setClasses] = useState([]);
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);
  const [detailStudents, setDetailStudents] = useState([]);
  const [detailStudentsLoading, setDetailStudentsLoading] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    feeTypeId: null,
    classId: null,
    acadyr: null,
    boardingType: null,
    isActive: null,
    searchText: "",
  });

  /**
   * Initial data load - once on mount
   */
  useEffect(() => {
    const initializeData = async () => {
      fetchFeeStructures();
      fetchFeeTypes({ filters: { is_active: 1 } });
      fetchAcademicYears();
      fetchClasses();
    };

    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Fetch classes
   */
  const fetchClasses = async () => {
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        `${domainSettings.backend}/bootstrap/klass`,
        null,
        navigate,
      );
      if (response.ok) {
        setClasses(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  const handleAddFeeStructure = () => {
    setEditingFeeStructure(null);
    setModalVisible(true);
  };

  const handleEditFeeStructure = (feeStructure) => {
    setEditingFeeStructure(feeStructure);
    setModalVisible(true);
  };

  const handleModalSuccess = () => {
    setModalVisible(false);
    setEditingFeeStructure(null);
    fetchFeeStructures();
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingFeeStructure(null);
  };

  const handleViewDetails = async (feeStructure) => {
    setSelectedFeeStructure(feeStructure);
    setDetailDrawerVisible(true);
    setDetailStudents([]);
    setDetailStudentsLoading(true);

    const students = await fetchStudentsAssignedToFeeStructure(
      feeStructure.feeStructureId,
    );
    setDetailStudents(students);
    setDetailStudentsLoading(false);
  };

  const handleActivate = async (id) => {
    await activateFeeStructure(id);
  };

  const handleDeactivate = async (id) => {
    await deactivateFeeStructure(id);
  };

  /**
   * Handle filter changes - refetch with server-side filters
   * Debounces search to avoid spam requests
   */
  useEffect(() => {
    // Clear previous timer
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }

    // Build filter object - only include non-null values
    // API expects snake_case parameter names
    const serverFilters = {};
    if (filters.feeTypeId !== null && filters.feeTypeId !== undefined)
      serverFilters.fee_type_id = filters.feeTypeId;
    if (filters.classId !== null && filters.classId !== undefined)
      serverFilters.school_class_id = filters.classId;
    if (filters.acadyr !== null && filters.acadyr !== undefined)
      serverFilters.acadyr = filters.acadyr;
    if (filters.boardingType !== null && filters.boardingType !== undefined)
      serverFilters.boarding_type = filters.boardingType;
    if (filters.isActive !== null && filters.isActive !== undefined)
      serverFilters.is_active = filters.isActive;

    // Debounce to avoid spam on rapid changes (500ms)
    const timer = setTimeout(() => {
      fetchFeeStructures({ filters: serverFilters });
    }, 500);

    setSearchDebounceTimer(timer);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [
    filters.feeTypeId,
    filters.classId,
    filters.acadyr,
    filters.boardingType,
    filters.isActive,
    filters.searchText,
    fetchFeeStructures,
  ]);

  const filteredFeeStructures = feeStructures.filter((feeStructure) => {
    if (filters.searchText) {
      const search = filters.searchText.toLowerCase();
      return (
        feeStructure.name?.toLowerCase().includes(search) ||
        feeStructure.description?.toLowerCase().includes(search) ||
        feeStructure.feeTypeName?.toLowerCase().includes(search) ||
        feeStructure.schoolClassName?.toLowerCase().includes(search) ||
        feeStructure.boardingType?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const stats = {
    total: feeStructures.length,
    active: feeStructures.filter((f) => f.isActive === 1).length,
    inactive: feeStructures.filter((f) => f.isActive === 0).length,
    totalAmount: feeStructures
      .filter((f) => f.isActive === 1)
      .reduce((sum, f) => sum + parseFloat(f.amount || 0), 0),
  };

  return (
    <>
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* Statistics */}
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Fee Structures"
                value={stats.total}
                valueStyle={{ color: "#1890ff" }}
                prefix={<UnorderedListOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card>
              <Statistic
                title="Active"
                value={stats.active}
                valueStyle={{ color: "#52c41a" }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card>
              <Statistic
                title="Inactive"
                value={stats.inactive}
                valueStyle={{ color: "#faad14" }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Active Amount"
                value={FinanceService.formatCurrency(stats.totalAmount)}
              />
            </Card>
          </Col>
        </Row>

        {/* Main Card */}
        <Card
          title={
            <Space>
              <UnorderedListOutlined />
              <span>Fee Structures Management</span>
            </Space>
          }
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddFeeStructure}
            >
              Add Fee Structure
            </Button>
          }
        >
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            {/* Filters */}
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} lg={6}>
                <Input
                  placeholder="Search fee structures..."
                  prefix={<SearchOutlined />}
                  value={filters.searchText}
                  onChange={(e) =>
                    setFilters({ ...filters, searchText: e.target.value })
                  }
                  allowClear
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Select
                  placeholder="Filter by Fee Type"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.feeTypeId}
                  onChange={(value) =>
                    setFilters({ ...filters, feeTypeId: value })
                  }
                >
                  {feeTypes.map((type) => (
                    <Option key={type.feeTypeId} value={type.feeTypeId}>
                      {type.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Select
                  placeholder="Filter by Class"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.classId}
                  onChange={(value) =>
                    setFilters({ ...filters, classId: value })
                  }
                >
                  {classes.map((cls) => (
                    <Option key={cls.id} value={cls.id}>
                      {cls.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Select
                  placeholder="Filter by Academic Year"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.acadyr}
                  onChange={(value) =>
                    setFilters({ ...filters, acadyr: value })
                  }
                >
                  {Array.isArray(academicYears) &&
                    academicYears.map((year) => (
                      <Option key={year.id} value={year.acadyr}>
                        {year.acadyr}
                      </Option>
                    ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Select
                  placeholder="Filter by Day/Boarding"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.boardingType}
                  onChange={(value) =>
                    setFilters({ ...filters, boardingType: value })
                  }
                >
                  <Option value="Both">Both</Option>
                  <Option value="Day">Day Students</Option>
                  <Option value="Boarding">Boarding Students</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Select
                  placeholder="Filter by Status"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.isActive}
                  onChange={(value) =>
                    setFilters({ ...filters, isActive: value })
                  }
                >
                  <Option value={1}>Active</Option>
                  <Option value={0}>Inactive</Option>
                </Select>
              </Col>
            </Row>

            {/* Results Summary */}
            {(filters.searchText ||
              filters.feeTypeId ||
              filters.classId ||
              filters.acadyr ||
              filters.boardingType ||
              filters.isActive !== null) && (
              <div>
                <Tag color="blue">
                  Showing {filteredFeeStructures.length} of{" "}
                  {feeStructures.length} fee structures
                </Tag>
              </div>
            )}

            {/* Table */}
            <FeeStructuresTable
              feeStructures={filteredFeeStructures}
              loading={loading}
              onView={handleViewDetails}
              onEdit={handleEditFeeStructure}
              onDelete={deleteFeeStructure}
              onActivate={handleActivate}
              onDeactivate={handleDeactivate}
            />
          </Space>
        </Card>
      </Space>

      <FeeStructureModal
        visible={modalVisible}
        onCancel={handleModalCancel}
        onSuccess={handleModalSuccess}
        feeStructure={editingFeeStructure}
      />

      <FeeStructureDetailDrawer
        visible={detailDrawerVisible}
        onClose={() => setDetailDrawerVisible(false)}
        feeStructureData={selectedFeeStructure}
        onEdit={handleEditFeeStructure}
        onDelete={deleteFeeStructure}
        onActivate={handleActivate}
        onDeactivate={handleDeactivate}
        students={detailStudents}
        studentsLoading={detailStudentsLoading}
      />
    </>
  );
};

export default FeeStructuresManagement;
